// FassalkartAI — Canonical language registry (CONTRACT §3.1)
//
// This is the SINGLE SOURCE OF TRUTH for language data on the client.
// Everything language-shaped is either defined here or re-exported from here,
// so there is exactly ONE import site going forward:
//
//     import { LANGUAGES, getBaseCode, BASE_LANGUAGES } from '../i18n/languages';
//
// The values below MUST stay byte-identical to the server copy at
// functions/translation/languages.js. If you change one, change both.
//
// It also owns the ONE authoritative in-memory store for the currently
// selected language. LanguageContext and AppContext are both thin views over
// this store — that is what keeps `t(hi, en)` (AppContext) and `t.nav.prices`
// (LanguageContext) from drifting apart. The store lives outside React on
// purpose: AppProvider is mounted *outside* LanguageProvider in App.js, so a
// context-to-context dependency is impossible in that direction.

import T, {
  LANGUAGES,
  VOICE_CODES,
  RTL_LANGS,
  LANG_GROUPS,
  AI_INSTRUCTION,
  getBaseCode,
  getFallback
} from './translations';

// ─── Contract §3.1 — the canonical lists ──────────
// 16 base languages. Order is meaningful: it is the display order and the
// order the translation trigger fans out in.
export const BASE_LANGUAGES = [
  'en', 'hi', 'bn', 'mr', 'gu', 'pa', 'ta', 'te',
  'kn', 'ml', 'or', 'ne', 'as', 'ur', 'ks', 'mni'
];

// 15 romanized ("mixed") modes — same language, Latin script + English mix.
// No `en-en`: English is already Latin.
export const ROMAN_LANGUAGES = [
  'hi-en', 'bn-en', 'mr-en', 'gu-en', 'pa-en', 'ta-en', 'te-en', 'kn-en',
  'ml-en', 'or-en', 'ne-en', 'as-en', 'ur-en', 'ks-en', 'mni-en'
];

// What server-side auto-translation targets. Romanized modes are NOT
// translated — they are rendered from their base language at read time.
export const CONTENT_LANGUAGES = BASE_LANGUAGES;

// Google Cloud Translation v3 target codes. Identical to our codes except
// Manipuri, which GCT only accepts as `mni-Mtei` (Meetei Mayek).
export const GCT_CODE = {
  en: 'en',  hi: 'hi',  bn: 'bn',  mr: 'mr',
  gu: 'gu',  pa: 'pa',  ta: 'ta',  te: 'te',
  kn: 'kn',  ml: 'ml',  or: 'or',  ne: 'ne',
  as: 'as',  ur: 'ur',  ks: 'ks',  mni: 'mni-Mtei'
};

// Every selectable code (31).
export const ALL_LANGUAGE_CODES = [...BASE_LANGUAGES, ...ROMAN_LANGUAGES];

export const DEFAULT_LANGUAGE = 'en';

// ─── Storage keys ─────────────────────────────────
// ONE canonical key. `fassalkart_lang` wins because it is the key the navbar
// language picker has always written and the key src/services/kisanAI.js
// already reads.
export const LANG_STORAGE_KEY = 'fassalkart_lang';

// Read once at startup, then deleted. `fassalkartai_lang` was AppContext's
// private key (defaulted to 'hi'); `kisanai_lang` predates the rename.
export const LEGACY_LANG_STORAGE_KEYS = [
  'fassalkartai_lang',
  'kisanai_lang'
];

// ─── Validation / normalisation ───────────────────

const CODE_SET = new Set(ALL_LANGUAGE_CODES);

/**
 * True if `code` is one of the 31 selectable language codes.
 * @param {*} code
 * @returns {boolean}
 */
export function isValidLang(code) {
  return typeof code === 'string' && CODE_SET.has(code);
}

/**
 * Coerce anything into a usable language code.
 * Accepts BCP-47-ish input from the browser / profile ('hi-IN' -> 'hi').
 * @param {*} code
 * @param {string} [fallback]
 * @returns {string}
 */
export function normalizeLang(code, fallback = DEFAULT_LANGUAGE) {
  if (isValidLang(code)) return code;
  if (typeof code !== 'string' || !code) return fallback;

  const lower = code.trim().toLowerCase();
  if (isValidLang(lower)) return lower;

  // 'hi-IN' / 'hi_IN' -> 'hi'   (but keep our own 'hi-en' mixed codes)
  const head = lower.split(/[-_]/)[0];
  if (isValidLang(head)) return head;

  return fallback;
}

/**
 * Google Cloud Translation v3 code for one of our codes.
 * Romanized modes resolve to their base language.
 * @param {string} code
 * @returns {string|null} null when the language is not machine-translatable
 */
export function toGctCode(code) {
  const base = getBaseCode(normalizeLang(code));
  return GCT_CODE[base] || null;
}

/**
 * Inverse of toGctCode — maps a GCT code back to ours ('mni-Mtei' -> 'mni').
 * @param {string} gct
 * @returns {string|null}
 */
export function fromGctCode(gct) {
  if (typeof gct !== 'string') return null;
  const hit = Object.keys(GCT_CODE).find(k => GCT_CODE[k] === gct);
  return hit || (isValidLang(gct) ? gct : null);
}

/**
 * Like translations.js `getFallback()` but returns the language CODE instead
 * of the compiled T bundle. Content resolution needs a code, not a bundle.
 * @param {string} code
 * @returns {string}
 */
export function getFallbackCode(code) {
  const norm = normalizeLang(code);
  const base = getBaseCode(norm);
  if (T[base]) return base;
  if (T[norm]) return norm;
  return 'en';
}

/**
 * Ordered, de-duplicated list of language codes to try when resolving a
 * translated value: exact code -> base code -> compiled fallback -> English.
 * English terminates every chain (translations.js `getFallback()` ends at
 * `T.en`, so this is the same rule expressed as codes).
 * @param {string} code
 * @returns {string[]}
 */
export function getFallbackChain(code) {
  const norm = normalizeLang(code);
  const chain = [norm, getBaseCode(norm), getFallbackCode(norm), 'en'];
  return chain.filter((c, i) => c && chain.indexOf(c) === i);
}

/**
 * Language metadata row from LANGUAGES, never undefined.
 * @param {string} code
 * @returns {object}
 */
export function getLangMeta(code) {
  const norm = normalizeLang(code);
  return LANGUAGES.find(l => l.code === norm) || LANGUAGES[0];
}

/**
 * True for the 15 romanized "local + English" modes.
 * @param {string} code
 * @returns {boolean}
 */
export function isMixedLang(code) {
  return ROMAN_LANGUAGES.includes(normalizeLang(code));
}

/**
 * True when the language renders right-to-left (Urdu, Kashmiri).
 * @param {string} code
 * @returns {boolean}
 */
export function isRTLLang(code) {
  const norm = normalizeLang(code);
  return RTL_LANGS.has(norm) || RTL_LANGS.has(getBaseCode(norm));
}

// ─── Persistence ──────────────────────────────────

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    // Safari private mode / disabled storage throws on property access.
    return false;
  }
}

/**
 * Read the persisted language, migrating the legacy keys on first run so
 * existing users keep the language they already chose.
 * @returns {string|null}
 */
export function readStoredLanguage() {
  if (!hasStorage()) return null;
  try {
    const current = localStorage.getItem(LANG_STORAGE_KEY);
    if (isValidLang(current)) {
      // Canonical key wins; drop any leftovers so they can never diverge.
      LEGACY_LANG_STORAGE_KEYS.forEach(k => {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      });
      return current;
    }

    for (const key of LEGACY_LANG_STORAGE_KEYS) {
      const legacy = localStorage.getItem(key);
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      if (isValidLang(legacy)) {
        localStorage.setItem(LANG_STORAGE_KEY, legacy);
        return legacy;
      }
    }

    // A non-empty but invalid canonical value: normalise it if we can.
    if (current) {
      const norm = normalizeLang(current, null);
      if (norm) {
        localStorage.setItem(LANG_STORAGE_KEY, norm);
        return norm;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist the selected language.
 * @param {string} code
 */
export function writeStoredLanguage(code) {
  if (!hasStorage() || !isValidLang(code)) return;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // Quota / private mode — the in-memory store still has the right value.
  }
}

// ─── The one authoritative language store ─────────
// Plain observable, no React. Both contexts subscribe via
// useSyncExternalStore, so a change from ANY caller updates every consumer
// in the same tick.

let currentLanguage = null;
const listeners = new Set();

function ensureInit() {
  if (currentLanguage === null) {
    currentLanguage = readStoredLanguage() || DEFAULT_LANGUAGE;
  }
  return currentLanguage;
}

/**
 * Currently selected language code. Stable identity (a string), safe as a
 * useSyncExternalStore snapshot.
 * @returns {string}
 */
export function getLanguage() {
  return ensureInit();
}

/**
 * Set the selected language. This is the ONLY writer.
 * @param {string} code
 * @param {{ persist?: boolean }} [opts]
 * @returns {boolean} false when the code was rejected or unchanged
 */
export function setLanguage(code, opts = {}) {
  const { persist = true } = opts;
  if (!isValidLang(code)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[i18n] ignoring unknown language code:', code);
    }
    return false;
  }
  ensureInit();
  if (currentLanguage === code) {
    if (persist) writeStoredLanguage(code);
    return false;
  }

  currentLanguage = code;
  if (persist) writeStoredLanguage(code);

  listeners.forEach(fn => {
    try {
      fn(code);
    } catch (e) {
      console.error('[i18n] language listener failed:', e);
    }
  });
  return true;
}

/**
 * Subscribe to language changes. Returns an unsubscribe function.
 * @param {(code: string) => void} fn
 * @returns {() => void}
 */
export function subscribeLanguage(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Keep multiple tabs in sync — a farmer with the app open twice should not
// see two different languages.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (e) => {
    if (e.key !== LANG_STORAGE_KEY || !isValidLang(e.newValue)) return;
    // persist:false — the other tab already wrote it.
    setLanguage(e.newValue, { persist: false });
  });
}

// ─── Re-exports: the single import site ───────────
export {
  T,
  LANGUAGES,
  VOICE_CODES,
  RTL_LANGS,
  LANG_GROUPS,
  AI_INSTRUCTION,
  getBaseCode,
  getFallback
};
